import type { EditorView } from 'prosemirror-view';

import { fg } from '@confluence/feature-gating';
import {
	LIVE_SEARCH_SELECT_CLASSNAME_PREFIX,
	LIVE_SEARCH_BUTTON_CLASSNAME,
} from '@confluence/live-search/entry-points/liveSearchExtensionConstants';

export const handleLiveSearchInput = (_view: EditorView, event: KeyboardEvent | InputEvent) => {
	/**
	 * We have to use the event.target to indentify that we are dealing with the live search extension
	 * instead of the current editor selection because
	 *   1. We can’t guarantee the selection won’t change while we are in the extension
	 *   2. Often times the selection is not the extension despite currently interacting with the extension
	 *
	 * As a result, we have to rely on the event.target to identify if the current event is meant for the
	 * live search extension.
	 */
	const isLiveSearchExtensionElement =
		(event.target as HTMLElement).classList.contains(
			`${LIVE_SEARCH_SELECT_CLASSNAME_PREFIX}__input`,
		) || (event.target as HTMLElement).className === LIVE_SEARCH_BUTTON_CLASSNAME;

	return (
		(event.target instanceof HTMLInputElement || event.target instanceof HTMLButtonElement) &&
		isLiveSearchExtensionElement
	);
};

/**
 * When interacting with input elements inside an extension's body, the events
 * bubble up to the editor and get handled by it.
 *
 * This almost always gets in the way of being able to actually interact with said input in the extension, i.e.
 * typing inside a text field (in an extension body) will print the text in the editor
 * content area instead.
 *
 * These event handlers are to prevent the editor view from trying to handle these events,
 * for the live search extension in particular.
 */
export const getEventHandlersForLiveSearchExtension = () =>
	fg('cc_page_experiences_live_search_wysiwyg')
		? {
				keydown: handleLiveSearchInput,
				keyup: handleLiveSearchInput,
				keypress: handleLiveSearchInput,
				beforeinput: handleLiveSearchInput,
			}
		: {};
